import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import compact from 'lodash/compact'

export const useActiveUserSync = ({
  refetchGroup,
  groupWatch,
  currentUserId,
  socketConnected,
  emitGetActiveUsers,
  emitJoin,
}) => {
  const [userDeviceMap, setUserDeviceMap] = useState({})
  // This is simply for ordering. We don't need to filter out inactive users here.
  const [seenUsersInOrder, setSeenUsersInOrder] = useState([])

  const joinEmitted = useRef(false)

  const activeUsers = useMemo(
    () =>
      compact([
        ...seenUsersInOrder.filter((memberId) => userDeviceMap[memberId]?.length > 0),
        currentUserId,
      ]),
    [userDeviceMap, seenUsersInOrder, currentUserId],
  )

  // Tell others we've arrived
  useEffect(() => {
    if (socketConnected) {
      emitGetActiveUsers()
      if (!joinEmitted.current) {
        joinEmitted.current = true
        emitJoin()
      }
    }
  }, [emitGetActiveUsers, emitJoin, socketConnected])

  useEffect(() => {
    let timeout

    const synchroniseActiveUsers = (specifiedGroup = {}, attempt = 0) => {
      if (attempt > 3) return

      const { userList } = specifiedGroup

      // if a new user has just joined, the existing groupwatch object won't contain them, so need to refresh
      const unknownUserFound =
        userList &&
        activeUsers
          .filter((id) => id !== currentUserId)
          .some((id) => !userList.find((groupUser) => groupUser.memberId === id))

      if (unknownUserFound) {
        // we need to wait a few seconds before refreshing, or the API response will not contain the new user
        clearTimeout(timeout)
        timeout = setTimeout(() => {
          refetchGroup().then((updatedGroup) =>
            // it's possible that the guest who just joined is still not in the API response, so call recursively
            synchroniseActiveUsers(updatedGroup, attempt + 1),
          )
        }, 2000)
      }
    }
    synchroniseActiveUsers(groupWatch)
    return () => clearTimeout(timeout)
  }, [activeUsers, currentUserId, groupWatch, refetchGroup])

  const devicesFor = useCallback((memberId) => userDeviceMap[memberId] || [], [userDeviceMap])
  const isCurrentUser = useCallback((memberId) => memberId === currentUserId, [currentUserId])

  const setUserDevices = useCallback(
    (memberId, devices) => setUserDeviceMap({ ...userDeviceMap, [memberId]: compact(devices) }),
    [userDeviceMap],
  )

  const publicHandlers = useMemo(() => {
    const isNewUserDevicePair = ({ memberId, deviceId }) =>
      !isCurrentUser(memberId) && devicesFor(memberId).indexOf(deviceId) === -1

    return {
      isCurrentUser,
      isNewUserDevicePair,
      isActiveUser: (memberId) => activeUsers.includes(memberId),

      addActiveUser: ({ memberId, deviceId }) => {
        if (isCurrentUser(memberId)) return

        setSeenUsersInOrder((prev) => Array.from(new Set([memberId, ...prev])))

        if (isNewUserDevicePair({ memberId, deviceId })) {
          setUserDevices(memberId, [...devicesFor(memberId), deviceId])
        }
      },
      removeActiveUser: ({ memberId, deviceId }) => {
        setUserDevices(
          memberId,
          devicesFor(memberId).filter((id) => id !== deviceId),
        )
      },
    }
  }, [activeUsers, devicesFor, isCurrentUser, setUserDevices])

  return {
    activeUsers,
    ...publicHandlers,
  }
}
